import { CommonModule, isPlatformBrowser } from '@angular/common';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { afterNextRender, ChangeDetectionStrategy, Component, DestroyRef, EventEmitter, HostBinding, inject, Input, OnInit, Output, PLATFORM_ID, signal, viewChild } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router } from '@angular/router';
import { filter } from 'rxjs';
import { TranslateModule } from '@ngx-translate/core';
import { UserService } from '../../app/shared/services/user.service';
import { CommonService, UserProfileImageParams } from '../../app/shared/services/common.service';
import { UserProfileMenuComponent } from '../user-profile-menu/user-profile-menu.component';
import { LanguageMenuComponent } from '../language-menu/language-menu.component';
import { TooltipModule } from '@syncfusion/ej2-angular-popups';
import { IntentSearchComponent } from '../../shared-component/intent-search/app-intent-search.component';
import { SearchFilterSpec } from '../../app/shared/models/search-filter-spec.model';

/**
 * sessionStorage key the resolved header avatar is cached under. Kept in sync with
 * `UserService.resetUserServiceState`, which clears the same key on logout so the
 * next user never sees the previous user's avatar.
 */
const PROFILE_IMAGE_KEY = 'userProfileImage';

/**
 * Always-on app-shell header: the global search box, the quick-action icon row,
 * and the user-profile dropdown. Emits `search`/`clearSearch`/`action`/`menuToggle`
 * to the shell, and keeps the displayed user name + avatar in sync with
 * {@link UserService} on boot and on every completed navigation (so the post-login
 * redirect populates it without a manual refresh). The avatar is cache-guarded in
 * sessionStorage and browser-only.
 */
@Component({
  selector: 'app-top-bar-component',
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, TranslateModule, UserProfileMenuComponent, LanguageMenuComponent, TooltipModule, IntentSearchComponent],
  templateUrl: './top-bar-component.component.html',
  styleUrl: './top-bar-component.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TopBarComponentComponent implements OnInit {
  private readonly userService = inject(UserService);
  private readonly commonService = inject(CommonService);
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly destroyRef = inject(DestroyRef);

  /** Handle to the intent-search results dropdown (Task 12), so the raw `<input>`'s Enter/
      arrow-key handlers — the input is the only element that actually holds keyboard focus —
      can forward to it. `undefined` only in the impossible case the child hasn't rendered yet
      (it is unconditional in the template), so `?.` is defensive, not load-bearing. */
  private readonly intentSearch = viewChild(IntentSearchComponent);

  /** Header language picker; the whole UI is behind the translate pipe as of the 2026-09 i18n completion. */
  protected readonly languageMenuEnabled = true;

  /** Guards against firing a second profile-image request while one is in flight
      (rapid navigations can call {@link getUserImage} before the first resolves
      and caches). */
  private isFetchingImage = false;

  /** Whether the desktop sidebar is collapsed to its icons-only rail. Reflected
      onto the host as `.is-sidebar-collapsed` so the SCSS can drop the search
      box's width cap and let it grow into the space the rail freed. Only ever
      true at ≥1024px (the drawer breakpoint never toggles it), so the mobile
      layout is unaffected. */
  @Input() sidebarCollapsed = false;

  /**
   * Mirrors {@link sidebarCollapsed} onto the host element so the stylesheet's
   * `:host(.is-sidebar-collapsed)` rule can widen the search box. The class has to
   * sit on the host (not the inner `.top-bar`) because `:host()` is the only
   * selector the collapsed state is exposed through.
   * @returns {boolean} `true` while the desktop sidebar is the icons-only rail.
   */
  @HostBinding('class.is-sidebar-collapsed')
  get isSidebarCollapsedClass(): boolean { return this.sidebarCollapsed; }

  /** Name shown in the profile menu. Starts empty — pre-login, and mid-flight during
      the SSO `/auth-redirect` handoff, `UserService` has no real name yet, and a
      hardcoded placeholder (e.g. a real role name like "Audit Head") would render as
      if it were the signed-in user's actual name/role. `UserProfileMenuComponent`
      renders a shimmer instead of text while this is empty (see its template) — the
      same treatment the rest of the dashboard shell gives loading content, rather
      than a placeholder that could be mistaken for real data.
      A signal because `refreshUser()` writes it from the NavigationEnd subscription —
      outside any template event — and this component is OnPush, so a plain field
      would leave the header stale until the next event. */
  readonly userName = signal('');

  /** False until the first client render (i.e. hydration) has completed — see the
      constructor. Gates {@link refreshUser} the same way `AppComponent` gates the
      customer logo, and for the same reason. */
  private canResolveUser = false;

  /**
   * Resolving the user is DEFERRED to after the first render, never done inline in
   * `ngOnInit`.
   *
   * The server renders with an empty {@link userName}, so the HTML it ships contains the
   * profile menu's shimmer branch. `ngOnInit` runs during the hydrating creation pass, so
   * reading the name from sessionStorage there makes the client pick the NAME branch of
   * that `@if` instead — the server's shimmer `<span>` is then never claimed and sits in
   * the header next to the name until Angular's dehydrated-view cleanup runs at
   * application stability. That is the stray grey bar beside the user name (reported
   * 2026-09-02, same root cause as the sidebar logo's).
   *
   * `afterNextRender` is a no-op on the server, which is right — the SSR pass must keep
   * the shimmer.
   */
  constructor() {
    afterNextRender(() => {
      this.canResolveUser = true;
      this.refreshUser();
    });
  }

  /** Reactive search input model. `search` is emitted from its `valueChanges`
      (every keystroke) so the header uses reactive forms, not `[(ngModel)]`. */
  readonly queryControl = new FormControl('', { nonNullable: true });

  /** Fallback avatar asset (served from `public/`) shown on error / empty response. */
  private readonly DEFAULT_PROFILE = 'vectors/ProfileIcon.svg';

  /** Avatar shown in the header. Starts as the default so there is always a valid
      `src` (and the SSR pass renders it) until a real image resolves in the browser.
      A signal so the OnPush view re-renders when the async fetch resolves. */
  readonly userProfileUrl = signal(this.DEFAULT_PROFILE);

  @Output() search = new EventEmitter<string>();
  /**
   * Fires only on an explicit search *submit* (Enter key or the search-icon
   * button), carrying the current term. Distinct from {@link search}, which fires
   * on every keystroke — the shell navigates to the results page on submit, so it
   * must not react to every character typed.
   */
  @Output() searchSubmit = new EventEmitter<string>();
  @Output() clearSearch = new EventEmitter<void>();
  @Output() action = new EventEmitter<string>();
  @Output() menuToggle = new EventEmitter<void>();
  /** Fires when the user picks (Enter or click) an interpretation from the intent-search
      dropdown. Distinct from {@link searchSubmit} — {@link onSubmit} tries this path FIRST
      and only falls back to a plain-text {@link searchSubmit} when nothing is highlighted. */
  @Output() resultSelect = new EventEmitter<SearchFilterSpec>();

  /** Enter / search-icon click: tries the intent-search dropdown's highlighted row first
      (existing behaviour is otherwise unchanged) and only falls back to the pre-existing
      plain-text {@link searchSubmit} when nothing is highlighted (empty dropdown / empty
      query) — the EXACT case the pre-Task-12 behaviour covered. */
  onSubmit(): void {
    if (this.intentSearch()?.selectHighlighted()) {
      return;
    }
    this.searchSubmit.emit(this.queryControl.value);
  }

  /** Forwards ArrowDown/ArrowUp from the raw `<input>` — where keyboard focus actually lives —
      to the dropdown child, since the dropdown's own `<ul>` never receives focus itself. */
  onSearchArrowDown(): void { this.intentSearch()?.onArrowDown(); }
  onSearchArrowUp(): void { this.intentSearch()?.onArrowUp(); }

  /** Forwards focus/blur from the raw `<input>` to the dropdown child (Task 7) — same
      reasoning as {@link onSearchArrowDown}/{@link onSearchArrowUp}: keyboard/DOM focus lives on
      the raw input, not on the dropdown, so the child's `onFocus()`/`onBlur()` (which drive
      `showSuggestions` and the unmatched-query log check) have to be called from here. */
  onSearchFocus(): void { this.intentSearch()?.onFocus(); }
  onSearchBlur(): void { this.intentSearch()?.onBlur(); }

  /**
   * Initialises the header, then keeps it in sync with the current user.
   *
   * The header lives in the always-on app shell, so this component is created
   * ONCE at app boot — before the token-login / auth-redirect flow populates
   * `UserService`. Reading the user only here would leave the name + avatar blank
   * until a manual refresh (the reported bug). So we also re-read on every
   * completed navigation: after login the redirect flow navigates to
   * `/dashboard`, and that `NavigationEnd` refreshes the header — no refresh
   * needed. It's cheap and idempotent (name is a plain read; the avatar is
   * cache-guarded), and it also covers company-switch re-navigation.
   */
  ngOnInit(): void {
    // NOT `refreshUser()` here — see the constructor for why the first resolve is
    // deferred past hydration.

    // Emit the live search term on every edit (mirrors the previous
    // `(input)="search.emit(query)"`); `onClear` suppresses this event and emits
    // `clearSearch` instead.
    this.queryControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => this.search.emit(value));

    this.router.events
      .pipe(
        filter((e): e is NavigationEnd => e instanceof NavigationEnd),
        takeUntilDestroyed(this.destroyRef),
      )
      // Gated for the same reason the initial resolve is deferred: a `NavigationEnd` that
      // lands mid-hydration would fill the name before the header is hydrated and strand
      // the server's shimmer. The deferred call in the constructor covers the first
      // resolve, so nothing is lost by skipping until then.
      .subscribe(() => { if (this.canResolveUser) { this.refreshUser(); } });
  }

  /**
   * Syncs the displayed name + avatar with the current {@link UserService} state.
   * Always writes {@link userName}, including an empty read — this component
   * survives a same-tab identity transition (e.g. re-entering `/auth-redirect`,
   * which is not chromeless), so a stale name from the previous identity must
   * fall back to the shimmer rather than staying on screen.
   */
  private refreshUser(): void {
    this.userName.set(this.userService.getUsername());
    this.getUserImage();
  }

  /**
   * Resolves the header avatar. Order of resolution:
   * 1. If a value is cached in sessionStorage under {@link PROFILE_IMAGE_KEY}, use
   *    it directly and skip the network call.
   * 2. Otherwise fetch it from `/common/user-profile-image` for the current user.
   *    On a `200` with a non-empty base64 string, normalise it (use a `data:` URI
   *    as-is, else prefix `data:image/jpeg;base64,`) and cache it.
   * 3. On error / empty / invalid response, fall back to the default asset — and
   *    cache the fallback too, so we don't re-request every render.
   *
   * Browser-only: it reads/writes sessionStorage, which does not exist during the
   * SSR render pass (this is an SSR app), so it no-ops on the server.
   *
   * No-ops until a user is actually logged in (`user_id` falsy). The header shell
   * renders pre-login, and calling the authenticated endpoint then would either
   * fail or return nothing and cache the default over the real avatar — so the
   * post-login fetch would be permanently short-circuited by that cached default.
   * @returns {void}
   */
  getUserImage(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }

    const userId = this.userService.getUserId();
    if (!userId) {
      return;
    }

    const cachedImage = sessionStorage.getItem(PROFILE_IMAGE_KEY);
    if (cachedImage) {
      this.userProfileUrl.set(cachedImage);
      return;
    }

    if (this.isFetchingImage) {
      return;
    }
    this.isFetchingImage = true;

    const params: UserProfileImageParams = { user_id: userId };
    this.commonService.getUserProfileImage(params).subscribe({
      next: (response) => {
        const base64 = response.body?.result;
        const resolved = response.status === 200 && base64
          ? (base64.startsWith('data:image') ? base64 : `data:image/jpeg;base64,${base64}`)
          : this.DEFAULT_PROFILE;
        this.userProfileUrl.set(resolved);
        sessionStorage.setItem(PROFILE_IMAGE_KEY, resolved);
        this.isFetchingImage = false;
      },
      error: () => {
        this.userProfileUrl.set(this.DEFAULT_PROFILE);
        sessionStorage.setItem(PROFILE_IMAGE_KEY, this.DEFAULT_PROFILE);
        this.isFetchingImage = false;
      },
    });
  }

  onClear(): void {
    // Silent reset (no `valueChanges`) so clearing emits `clearSearch`, not a
    // `search` with an empty term.
    this.queryControl.setValue('', { emitEvent: false });
    this.clearSearch.emit();
  }

  /**
   * Forwards a picked interpretation to the shell, then clears the query box — same silent-reset
   * pattern as {@link onClear} (`emitEvent: false`, so this doesn't also fire a `search` event).
   * Without this, the picked term stayed in the box and `app-intent-search`'s results dropdown
   * (gated on its own `hasQuery` computed) stayed open over whatever page the selection landed
   * on/stayed on.
   */
  onResultSelect(spec: SearchFilterSpec): void {
    this.queryControl.setValue('', { emitEvent: false });
    this.resultSelect.emit(spec);
  }

  /**
   * Handles the dropdown child's `querySelect` (Task 7) — fired when the user picks a
   * suggestion or a recent-search entry. `IntentSearchComponent` cannot write back into its own
   * `query` input, so this mirrors the picked text into `queryControl`; that reuses the
   * EXISTING `[query]="queryControl.value"` binding to flow it back down into the child, which
   * then resolves it and emits `resultSelect` (handled by {@link onResultSelect} above, which
   * clears the box again — the net effect matches clicking an already-interpreted dropdown row).
   */
  onQuerySelect(query: string): void {
    this.queryControl.setValue(query);
  }
}
